import { withDefaultCurrency } from 'common/utils/formatUtils';
import {
  LifeInsuranceInsuranceRiskType,
  LifeInsuranceProductFragment,
  LifeInsuranceProductInput,
  ProductStatus,
} from 'generated/graphql';

import { ProductBase } from '../types';

export const getLifeInsuranceDefaultInsuranceRisks = (): LifeInsuranceInsuranceRiskType[] => [
  LifeInsuranceInsuranceRiskType.Death,
  LifeInsuranceInsuranceRiskType.PermanentInjuryConsequences,
  LifeInsuranceInsuranceRiskType.CriticalIllnesses,
  LifeInsuranceInsuranceRiskType.Disability,
];

export interface LifeInsuranceProductDetailType extends ProductBase {
  contractSignDate?: Date | null;
  contractEndDate?: Date | null;
  applicants: Array<{
    id: string;
    name: string | null;
    surname?: string | null;
    insurance?: {
      insuranceRisks: {
        type?: LifeInsuranceInsuranceRiskType | null;
        name?: string | null;
        annualCost?: {
          amount: number;
          currency: string;
        };
        insuredSum?: {
          amount: number;
          currency: string;
        };
      }[];
    } | null;
  }>;
  insurer: Array<{
    id: string;
    name: string;
    surname?: string | null;
  }>;
  savings?: {
    amount?: {
      amount: number;
      currency: string;
    };
    insuredSum?: {
      amount: number;
      currency: string;
    };
  } | null;
  surrenderValue?: {
    amount: number;
    currency: string;
  };
}

export type MapLifeInsuranceProductToInput = (product?: LifeInsuranceProductDetailType) => LifeInsuranceProductInput;

export const mapLifeInsuranceProductToInput: MapLifeInsuranceProductToInput = (
  product = {
    applicants: [],
    documents: [],
    enableFinancialAdvisorCare: true,
    enableNotifications: true,
    id: '',
    institution: {
      abbreviation: '',
      id: '',
      logoUrl: '',
      name: '',
      possibleAispNordigenConnection: false,
      smallLogoUrl: '',
      wideLogoUrl: '',
    },
    insurer: [],
    status: ProductStatus.Active,
    statusChangeDate: new Date(),
  },
) => {
  return {
    applicants: (
      product.applicants?.map((applicant) => ({
        insurance: {
          insuranceRisks:
            applicant.insurance?.insuranceRisks.map((insuranceRisk) => ({
              annualCost: withDefaultCurrency(insuranceRisk.annualCost),
              insuredSum: withDefaultCurrency(insuranceRisk.insuredSum),
              name: insuranceRisk.name ?? '',
              type: insuranceRisk.type ?? LifeInsuranceInsuranceRiskType.Other,
            })) ?? [],
        },
        isInsured: true,
        isInsuring: false,
        name: applicant.name ?? '',
        surname: applicant.surname ?? '',
      })) ?? []
    ).concat(
      product.insurer?.[0]
        ? [
            {
              insurance: {
                insuranceRisks: [],
              },
              isInsured: false,
              isInsuring: true,
              name: product.insurer[0].name ?? '',
              surname: product.insurer[0].surname ?? '',
            },
          ]
        : [],
    ),
    contractEndDate: product.contractEndDate,
    contractNumber: product.contractNumber,
    contractSignDate: product.contractSignDate,
    enableFinancialAdvisorCare: product.enableFinancialAdvisorCare,
    enableNotifications: product.enableNotifications,
    financialDetailsUpdatedAt: new Date(),
    institutionId: product.institution?.id,
    institutionName: product.institutionName,
    name: product.name,
    note: product.note,
    paymentInfo: {
      constantSymbol: product.paymentInfo?.constantSymbol ?? '',
      ibanForPayment: product.paymentInfo?.ibanForPayment ?? '',
      specificSymbol: product.paymentInfo?.specificSymbol ?? '',
      variableSymbol: product.paymentInfo?.variableSymbol ?? '',
    },
    paymentPeriod: {
      expectedAmount: withDefaultCurrency(product.paymentPeriod?.expectedAmount),
      periodCount: product.paymentPeriod?.periodCount,
      periodTypeCode: product.paymentPeriod?.periodTypeCode,
      referenceDate: product.paymentPeriod?.expectedDate,
    },
    savings: product.savings
      ? {
          amount: withDefaultCurrency(product.savings.amount),
          insuredSum: withDefaultCurrency(product.savings.insuredSum),
        }
      : null,
    status: product.status,
    statusChangeDate: product.statusChangeDate,
    surrenderValue: withDefaultCurrency(product.surrenderValue),
  };
};

export const mapLifeInsuranceProductToDetail = (
  product?: LifeInsuranceProductFragment,
): LifeInsuranceProductDetailType => {
  const insurer = product?.applicants.find((applicant) => applicant.isInsuring);
  return {
    applicants:
      product?.applicants
        ?.filter((applicant) => !!applicant.isInsured)
        .map((applicant) => ({
          id: applicant.id,
          insurance: {
            insuranceRisks:
              applicant.insurance?.insuranceRisks.map((insuranceRisk) => ({
                annualCost: withDefaultCurrency(insuranceRisk.annualCost),
                insuredSum: withDefaultCurrency(insuranceRisk.insuredSum),
                name: insuranceRisk.name,
                type: insuranceRisk.type,
              })) ?? [],
          },
          name: applicant.name,
          surname: applicant.surname,
        })) ?? [],
    contractEndDate: product?.contractEndDate,
    contractNumber: product?.contractNumber,
    contractSignDate: product?.contractSignDate,
    documents: product?.documents ?? [],
    enableFinancialAdvisorCare: product?.enableFinancialAdvisorCare ?? true,
    enableNotifications: product?.enableNotifications ?? true,
    financialDetailsUpdatedAt: product?.financialDetailsUpdatedAt ?? new Date(),
    id: product?.id ?? '',
    institution: product?.institution,
    institutionName: product?.institutionName ?? '',
    insurer: insurer ? [insurer] : [],
    name: product?.name,
    note: product?.note,
    paymentInfo: product?.paymentInfo,
    paymentPeriod: product?.paymentPeriod,
    savings: product?.savings
      ? {
          amount: withDefaultCurrency(product.savings.amount),
          insuredSum: withDefaultCurrency(product.savings.insuredSum),
        }
      : null,
    status: product?.status ?? ProductStatus.Active,
    statusChangeDate: product?.statusChangeDate ?? new Date(),
    surrenderValue: withDefaultCurrency(product?.surrenderValue),
  };
};
